import React, { lazy } from 'react';
import { isFeatureEnabled } from '../constants/adminFeatureFlags';

const EditPermissions = lazy(() =>
  import('../admin-views/permissions/EditPermissions')
);
const ListCustomIngredients = lazy(() =>
  import('../admin-views/customIngredients/ListCustomIngredients.jsx')
);
const AdminDashboardContainer = lazy(() =>
  import('../admin-views/dashboard/containers/AdminDashboardContainer')
);
const AdminProfileContainer = lazy(() =>
  import(
    '../admin-views/dashboard/adminProfile/container/AdminProfileContainer'
  )
);
const ClientsContainer = lazy(() =>
  import('../admin-views/clients/containers/ClientsContainer')
);
const InitialStrategyContainer = lazy(() =>
  import('../admin-views/initialStrategy/containers/InitialStrategyContainer')
);
const DietaryExclusionsContainer = lazy(() =>
  import(
    '../admin-views/dietaryExclusions/containers/DietaryExclusionsContainer'
  )
);
const HighLightedMealsContainer = lazy(() =>
  import('../admin-views/highLightedMeals/containers/HighLightedMealsContainer')
);
const FoodTagsContainer = lazy(() =>
  import('../admin-views/foodTags/containers/FoodTagsContainer')
);
const MealTagsContainer = lazy(() =>
  import('../admin-views/mealTags/containers/MealTagsContainer')
);
const ProductsContainer = lazy(() =>
  import('../admin-views/products/containers/ProductsContainer')
);

const FoodTagsCategoryContainer = lazy(() =>
  import('../admin-views/foodTagsCategory/containers/FoodTagsCategoryContainer')
);
const BrandedIngredientsContainer = lazy(() =>
  import(
    '../admin-views/brandedIngredients/containers/BrandedIngredientsContainer.jsx'
  )
);
const GenericIngredientsContainer = lazy(() =>
  import(
    '../admin-views/genericIngredients/containers/GenericIngredientsContainer.jsx'
  )
);
const MealsContainer = lazy(() =>
  import('../admin-views/meals/containers/MealsContainer')
);
const LogsContainer = lazy(() =>
  import('../admin-views/logs/containers/LogsContainer')
);
const AppleIAPContainer = lazy(() =>
  import('../admin-views/appleIAP/containers/AppleIAPContainer')
);
const CheckinAnalyticsContainer = lazy(() =>
  import('../admin-views/checkinAnalytics/containers/CheckinAnalyticsContainer')
);
const MealPlanAnalyticsContainer = lazy(() =>
  import(
    '../admin-views/mealPlanAnalytics/containers/mealPlanAnalyticsContainer'
  )
);
const WaterIntakeGoalContainer = lazy(() =>
  import('../admin-views/waterIntakeGoal/WaterIntakeGoalContainer')
);
const GlobalConfigContainer = lazy(() =>
  import('../admin-views/globalConfig/GlobalConfigContainer')
);
const ProductPurchasesContainer = lazy(() =>
  import('../admin-views/productPurchases/containers/ProductPurchasesContainer')
);
const LinkBrandedIngredientsContainer = lazy(() =>
  import(
    '../admin-views/linkBrandedIngredients/containers/LinkBrandedIngredientsContainer'
  )
);
const AlternativeIngredientsContainer = lazy(() =>
  import(
    '../admin-views/alternativeIngredients/containers/AlternativeIngredientsContainer'
  )
);
const AiCoachBetaContainer = lazy(() =>
  import('../admin-views/aiCoachBeta/containers/AiCoachBetaContainer')
);
const AiCoachFeedbackContainer = lazy(() =>
  import('../admin-views/aiCoachFeedback/containers/AiCoachFeedbackContainer')
);
const TenantReportsContainer = lazy(() =>
  import('../admin-views/tenantReports/containers/TenantReportsContainer')
);
const EngagementInsightsContainer = lazy(() =>
  import('../admin-views/tenantReports/containers/EngagementInsightsContainer')
);
const ApiPerformanceContainer = lazy(() =>
  import('../admin-views/tenantReports/containers/ApiPerformanceContainer')
);
const ApiKeyContainer = lazy(() =>
  import('../admin-views/tenantReports/containers/ApiKeyContainer')
);

const AdminThemeRoutes = [
  {
    collapse: true,
    visible: false,
    state: 'dashboard',
    path: '/admin/dashboard',
    name: 'Filters',
    mini: 'B',
    permissions: 'basic_admin',
    roles: 'super_admin',
    icon: 'mdi mdi-view-dashboard',
    navFlagKey: 'filters',
    child: [
      {
        path: '/admin/dashboard/newClients',
        name: 'New Clients',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'newClients',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'newClients',
      },
      {
        path: '/admin/dashboard/userExtras',
        name: 'Clients With Add-Ons',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'hasConsumableProduct',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'clientsWithAddOns',
      },
      {
        path: '/admin/dashboard/checkInToday',
        name: 'Check In Today',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'checkInToday',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'checkInToday',
      },
      {
        path: '/admin/dashboard/preapproved',
        name: 'Pre-Approved Meal Plan',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'preapproved',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'preApprovedMealPlan',
      },
      {
        path: '/admin/dashboard/international',
        name: 'International Clients',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'internationalClients',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'internationalClients',
      },
      {
        path: '/admin/dashboard/taggedUsers',
        name: 'Tagged Clients',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        filter: 'taggedClients',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'taggedClients',
      },
      {
        path: '/admin/dashboard/all',
        name: 'All Filters',
        mini: 'B',
        icon: 'mdi mdi-view-dashboard',
        permissions: 'basic_admin',
        roles: 'super_admin',
        filter: 'all',
        component: AdminDashboardContainer,
        flagSection: 'filters',
        flagKey: 'allFilters',
      },
    ],
  },
  {
    path: '/admin/clients',
    name: 'Clients',
    state: 'clients',
    icon: 'icon-people',
    mini: 'B',
    permissions: 'basic_admin',
    roles: 'super_admin',
    component: ClientsContainer,
    exact: true,
    navFlagKey: 'clients',
  },
  {
    collapse: true,
    visible: false,
    path: '/admin/data',
    name: 'Data',
    state: 'data',
    permissions: 'basic_admin',
    roles: 'super_admin',
    icon: 'mdi mdi-gauge',
    navFlagKey: 'data',
    child: [
      {
        path: '/admin/data/branded-ingredients',
        name: 'Branded Ingredients',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_ingredients',
        roles: 'super_admin',
        component: BrandedIngredientsContainer,
        flagSection: 'data',
        flagKey: 'brandedIngredients',
      },
      {
        path: '/admin/data/generic-ingredients',
        name: 'Generic Ingredients',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_ingredients',
        roles: 'super_admin',
        component: GenericIngredientsContainer,
        flagSection: 'data',
        flagKey: 'genericIngredients',
      },
      {
        path: '/admin/data/customingredients',
        name: 'Custom Ingredients',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_custom_ingredients',
        roles: 'super_admin',
        component: ListCustomIngredients,
        exact: true,
        flagSection: 'data',
        flagKey: 'customIngredients',
      },
      {
        path: '/admin/data/meals',
        name: 'Meals',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_meals',
        roles: 'super_admin',
        component: MealsContainer,
        flagSection: 'data',
        flagKey: 'meals',
      },
      {
        path: '/admin/data/dietaryExclusions',
        name: 'Dietary Exclusions',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_dietary_exclusions',
        roles: 'super_admin',
        component: DietaryExclusionsContainer,
        flagSection: 'data',
        flagKey: 'dietaryExclusions',
      },
      {
        path: '/admin/data/highlightedMeals',
        name: 'Highlighted Meals',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: HighLightedMealsContainer,
        flagSection: 'data',
        flagKey: 'highlightedMeals',
      },
      {
        path: '/admin/data/foodTags',
        name: 'Food Tags',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_food_tags',
        roles: 'super_admin',
        component: FoodTagsContainer,
        flagSection: 'data',
        flagKey: 'foodTags',
      },
      {
        path: '/admin/data/categoryFoodTags',
        name: 'Food Tags Category',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_food_tags_category',
        roles: 'super_admin',
        component: FoodTagsCategoryContainer,
        flagSection: 'data',
        flagKey: 'foodTagsCategory',
      },
      {
        path: '/admin/data/mealTags',
        name: 'Meal Tags',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'data_meal_tags',
        roles: 'super_admin',
        component: MealTagsContainer,
        flagSection: 'data',
        flagKey: 'mealTags',
      },
    ],
  },
  {
    collapse: true,
    visible: false,
    path: '/admin/tools',
    name: 'Tools',
    state: 'tools',
    icon: 'mdi mdi-ruler',
    permissions: 'basic_admin',
    roles: 'super_admin',
    navFlagKey: 'tools',
    child: [
      {
        path: '/admin/tools/initialStrategy',
        name: 'Initial Strategy',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'tools_initial_strategy',
        roles: 'super_admin',
        component: InitialStrategyContainer,
        flagSection: 'tools',
        flagKey: 'initialStrategy',
      },
      {
        path: '/admin/tools/waterIntakeGoal',
        name: 'Water Intake Goal',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_water_intake_goal',
        roles: 'super_admin',
        component: WaterIntakeGoalContainer,
        flagSection: 'tools',
        flagKey: 'waterIntakeGoal',
      },
      {
        path: '/admin/tools/audit',
        name: 'Administrative Audit',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'tools_administrative_audit',
        roles: 'super_admin',
        component: LogsContainer,
        flagSection: 'tools',
        flagKey: 'administrativeAudit',
      },
    ],
  },
  {
    collapse: true,
    visible: false,
    path: '/admin/adminTools',
    name: 'Admin Tools',
    state: 'admintools',
    icon: 'icon-screen-desktop',
    permissions: 'basic_admin',
    roles: 'super_admin',
    navFlagKey: 'adminTools',
    child: [
      {
        path: '/admin/adminTools/analytics',
        name: 'Checkin Analytics',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_checkin_analytics',
        roles: 'super_admin',
        component: CheckinAnalyticsContainer,
        flagSection: 'adminTools',
        flagKey: 'checkinAnalytics',
      },
      {
        path: '/admin/adminTools/productPurchases',
        name: 'Product Purchases',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_checkin_analytics',
        roles: 'super_admin',
        component: ProductPurchasesContainer,
        flagSection: 'adminTools',
        flagKey: 'productPurchases',
      },
      {
        path: '/admin/adminTools/applePayments',
        name: 'Apple In-App Purchases',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_apple_in_app_purchases',
        roles: 'super_admin',
        component: AppleIAPContainer,
        flagSection: 'adminTools',
        flagKey: 'appleInAppPurchases',
      },
      {
        path: '/admin/adminTools/approvalAnalytics',
        name: 'Meal Plan Approvals',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_meal_plan_approvals',
        roles: 'super_admin',
        component: MealPlanAnalyticsContainer,
        flagSection: 'adminTools',
        flagKey: 'mealPlanApprovals',
      },
      {
        path: '/admin/adminTools/products',
        name: 'Products',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_products',
        roles: 'super_admin',
        component: ProductsContainer,
        flagSection: 'adminTools',
        flagKey: 'products',
      },
      {
        path: '/admin/adminTools/globalConfig',
        name: 'Global Configurations',
        mini: 'B',
        icon: 'mdi mdi-adjust',
        permissions: 'admin_tools_products',
        roles: 'super_admin',
        component: GlobalConfigContainer,
        flagSection: 'adminTools',
        flagKey: 'globalConfigurations',
      },
      {
        path: '/admin/adminTools/aiCoachBeta',
        name: 'AI Coach Beta Access',
        mini: 'B',
        icon: 'mdi mdi-robot',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AiCoachBetaContainer,
        flagSection: 'adminTools',
        flagKey: 'aiCoachBetaAccess',
      },
      {
        path: '/admin/adminTools/aiCoachFeedback',
        name: 'AI Coach Feedback',
        mini: 'F',
        icon: 'mdi mdi-robot-happy',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: AiCoachFeedbackContainer,
        flagSection: 'adminTools',
        flagKey: 'aiCoachFeedback',
      },
    ],
  },
  {
    collapse: true,
    visible: false,
    path: '/admin/approvals',
    name: 'Approvals',
    state: 'approvals',
    icon: 'mdi mdi-check-all',
    permissions: 'basic_admin',
    roles: 'super_admin',
    navFlagKey: 'approvals',
    child: [
      {
        path: '/admin/approvals/link-branded-ingredients',
        name: 'Link Branded Ingredients',
        mini: 'B',
        icon: 'fas fa-link',
        permissions: 'data_ingredients',
        roles: 'super_admin',
        component: LinkBrandedIngredientsContainer,
        flagSection: 'approvals',
        flagKey: 'linkBrandedIngredients',
      },
      {
        path: '/admin/approvals/alternative-ingredients',
        name: 'Alternative Ingredients',
        mini: 'B',
        icon: 'fas fa-exchange-alt',
        permissions: 'data_ingredients',
        roles: 'super_admin',
        component: AlternativeIngredientsContainer,
        flagSection: 'approvals',
        flagKey: 'alternativeIngredients',
      },
    ],
  },
  {
    collapse: true,
    visible: false,
    path: '/admin/tenants',
    name: 'Tenants',
    state: 'tenants',
    icon: 'mdi mdi-domain',
    permissions: 'basic_admin',
    roles: 'super_admin',
    navFlagKey: 'tenants',
    child: [
      {
        path: '/admin/tenants/engagement-insights',
        name: 'Engagement Insights',
        mini: 'E',
        icon: 'mdi mdi-chart-line',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: EngagementInsightsContainer,
        flagSection: 'tenants',
        flagKey: 'reports',
      },
      {
        path: '/admin/tenants/api-usage',
        name: 'API Usage',
        mini: 'P',
        icon: 'mdi mdi-gauge',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: ApiPerformanceContainer,
        flagSection: 'tenants',
        flagKey: 'reports',
      },
      {
        path: '/admin/tenants/api-key',
        name: 'API Key',
        mini: 'K',
        icon: 'mdi mdi-key-variant',
        permissions: 'basic_admin',
        roles: 'super_admin',
        component: ApiKeyContainer,
        flagSection: 'tenants',
        flagKey: 'reports',
      },
      // {
      //   path: '/admin/tenants/reports',
      //   name: 'Reports',
      //   mini: 'R',
      //   icon: 'mdi mdi-chart-line',
      //   permissions: 'basic_admin',
      //   roles: 'super_admin',
      //   component: TenantReportsContainer,
      //   flagSection: 'tenants',
      //   flagKey: 'reports',
      // },
    ],
  },
];

// Prunes nav sections/children that are turned off via admin_feature_flags,
// before permission/role gating runs in sidebar.jsx and adminlayout.jsx.
export function filterRoutesByFeatureFlags(routes, flags) {
  return routes.reduce((visibleRoutes, route) => {
    if (route.navFlagKey && !isFeatureEnabled(flags, 'nav', route.navFlagKey)) {
      return visibleRoutes;
    }
    if (route.collapse && Array.isArray(route.child)) {
      const child = route.child.filter(
        (childRoute) =>
          !childRoute.flagSection ||
          isFeatureEnabled(flags, childRoute.flagSection, childRoute.flagKey)
      );
      if (child.length === 0) {
        return visibleRoutes;
      }
      visibleRoutes.push({ ...route, child });
      return visibleRoutes;
    }
    visibleRoutes.push(route);
    return visibleRoutes;
  }, []);
}

export const clientRoute = {
  path: '/admin/clients/:profileId',
  name: 'Clients',
  state: 'clients',
  icon: 'icon-people',
  mini: 'B',
  component: ClientsContainer,
};

export const profileEditorRoute = {
  path: '/admin/account',
  name: 'Account',
  state: 'dashboardpages',
  icon: 'mdi mdi-account',
  mini: 'B',
  component: AdminProfileContainer,
};

export const permissionsEditorRoute = {
  path: '/admin/adminTools/permissions/:id',
  name: 'Permissions',
  state: 'permissions',
  icon: 'icon-people',
  mini: 'B',
  component: EditPermissions,
  exact: true,
};

export default AdminThemeRoutes;
